/**
 * Clerk user sync component.
 *
 * After Clerk authentication completes:
 *   1. Gets the active Clerk session token
 *   2. Calls funnel-api `POST /api/clerk/signup` with the Clerk token
 *   3. Syncs the user to ndapi (`POST /v2/users`) — fire-and-forget
 *   4. Stores the sign-in token from the response in answers for handoff
 *   5. Auto-advances
 *
 * On error the step writes `skipped_error` and advances so the funnel is
 * never blocked.
 */
import { useSession } from "@clerk/clerk-react";
import type { UserSyncData } from "@nextday/survey-kit";
import type { RendererProps } from "@nextday/survey-kit";
import { useCallback, useEffect, useRef, type ReactElement } from "react";

import {
  createNdapiUser,
  resolveDisplayName,
  safeReadAttributionContext,
} from "@src/auth/ndapiSync";
import {
  CLERK_SIGNIN_TOKEN_ANSWER_KEY,
  CLERK_EXTERNAL_SIGNIN_URL_KEY,
} from "@src/funnel/clerkConstants";

export { CLERK_SIGNIN_TOKEN_ANSWER_KEY, CLERK_EXTERNAL_SIGNIN_URL_KEY };

const CLERK_SIGNUP_API = "/api/clerk/signup";

const buildRequestExtras = () => {
  const ctx = safeReadAttributionContext();
  if (!ctx) return {};
  const trackingData: Record<string, string | undefined> = {};
  if (ctx.partners?.tapVid) trackingData.tapClickId = ctx.partners.tapVid;
  if (ctx.partners?.crakRevenueClickId) trackingData.crid = ctx.partners.crakRevenueClickId;
  if (ctx?.lastTouch?.platformDetails?.extraParams?.rtkcid) {
    trackingData.rtClickId = ctx?.lastTouch?.platformDetails?.extraParams?.rtkcid;
  }
  const affCoupon = ctx.partners?.affiliateId;
  return {
    ...(Object.keys(trackingData).length > 0 ? { trackingData } : {}),
    ...(affCoupon ? { affCoupon } : {}),
  };
};

export const ClerkUserSyncComponent = ({
  data,
  answers,
  session: funnelSession,
  onAdvance,
  onAnswer,
}: RendererProps<UserSyncData>): ReactElement => {
  const { session: clerkSession, isLoaded } = useSession();
  const triggeredRef = useRef(false);
  const userAlreadyExists = answers.user_exists === true;

  const doSync = useCallback(async () => {
    if (!isLoaded) return;

    if (userAlreadyExists) {
      onAdvance();
      return;
    }

    if (!clerkSession) {
      onAnswer(data.answerKey, { value: "skipped_error" });
      onAdvance();
      return;
    }

    try {
      const token = await clerkSession.getToken();
      if (!token) {
        onAnswer(data.answerKey, { value: "skipped_error" });
        onAdvance();
        return;
      }

      const extras = buildRequestExtras();
      const rawDisplayName = resolveDisplayName(answers, data.nameAnswerKey);

      const res = await fetch(CLERK_SIGNUP_API, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({
          applicationId: funnelSession.application_id,
          clerkToken: token,
          ...extras,
          ...(rawDisplayName && { profileName: rawDisplayName }),
        }),
      });

      if (!res.ok) {
        onAnswer(data.answerKey, { value: "skipped_error" });
        onAdvance();
        return;
      }

      const result = (await res.json()) as {
        token?: string;
        userId?: string;
        isNewUser?: boolean;
        externalSigninUrl?: string;
        username?: string;
      };

      // Fire-and-forget ndapi sync so the product backend has the user
      // record and attribution context.  Failures are swallowed — the
      // handoff must never be blocked by ndapi availability.
      const clerkUserId = clerkSession.user?.id;
      const clerkEmail =
        clerkSession.user?.primaryEmailAddress?.emailAddress ??
        clerkSession.user?.emailAddresses?.[0]?.emailAddress;

      if (result.isNewUser && clerkUserId && clerkEmail) {
        const attribution = safeReadAttributionContext();
        const displayName = rawDisplayName || "Honey";
        void createNdapiUser(
          token,
          funnelSession.application_id,
          funnelSession.anonymous_id,
          displayName,
          { id: clerkUserId, email: clerkEmail },
          attribution,
          result.username,
        ).catch(() => {
          // Swallowed — ndapi sync is best-effort.
        });
      }

      if (result.token) {
        onAnswer(CLERK_SIGNIN_TOKEN_ANSWER_KEY, { value: result.token });
      }
      if (result.externalSigninUrl) {
        onAnswer(CLERK_EXTERNAL_SIGNIN_URL_KEY, { value: result.externalSigninUrl });
      }
      onAnswer("user_exists", { value: true });
      onAnswer(data.answerKey, { value: result.isNewUser ? "created" : "exists" });
      onAdvance();
    } catch {
      onAnswer(data.answerKey, { value: "skipped_error" });
      onAdvance();
    }
  }, [
    isLoaded,
    userAlreadyExists,
    clerkSession,
    funnelSession.application_id,
    funnelSession.anonymous_id,
    answers,
    data.answerKey,
    data.nameAnswerKey,
    onAnswer,
    onAdvance,
  ]);

  useEffect(() => {
    if (!isLoaded || triggeredRef.current) return;
    triggeredRef.current = true;
    void doSync();
  }, [isLoaded, doSync]);

  return (
    <div className="flex flex-col items-center gap-3 p-6" role="status" aria-label="Syncing">
      <div className="size-8 animate-spin rounded-full border-2 border-white/20 border-t-white" />
      <p className="text-sm opacity-80">{data.message ?? "Setting up your profile\u2026"}</p>
    </div>
  );
};
